import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import { ModalTitle } from '~/components/views/common/Modal';
import { PortalModal } from '~/components/views/common/Modal/Modal';
import PrimaryButton from '~/components/views/common/buttons/PrimaryButton/PrimaryButton';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import formatMessageWrapper from '~/utils/format-message-wrapper/format-message-wrapper';
import {
  toggleModalOpen as toggleModalOpenAction,
  setLastReadChangeLogItemDate as setLastReadChangeLogItemDateAction,
  loadChangeLog as loadChangeLogAction,
} from '../../store/reducer';
import { useGetUnreadChangeLogItems } from '~/domain/changelog/hooks/use-get-unread-changelog-items';
import ChangeLogItemRow from '~/domain/changelog/components/ChangeLogItemRow/ChangeLogItemRow';
import * as S from './Styled';

export interface Props {
  isModalOpen: boolean;
  loadChangeLog: () => void;
  toggleModalOpen: () => void;
  setLastReadChangeLogItemDate: (payload: { date: string }) => void;
}

export const ChangeLogModalContainer = ({
  setLastReadChangeLogItemDate,
  loadChangeLog,
  toggleModalOpen,
  ...props
}: Props) => {
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  const { items: unreadChangeLogItems } = useGetUnreadChangeLogItems();
  const unseenChangeLogItems = unreadChangeLogItems.filter(
    (item) => formatMessageWrapper(item.title) !== item.title
  );
  const [logItemIndex, setLogItemIndex] = useState(0);
  const activeItem = unseenChangeLogItems[logItemIndex];
  const hasMoreLogItems = unseenChangeLogItems.length > logItemIndex + 1;

  const setNextLogItemIndex = useCallback(() => {
    setLogItemIndex((state) => state + 1);
  }, []);

  const onClose = useCallback(() => {
    if (unseenChangeLogItems.length > 0) {
      setLastReadChangeLogItemDate({
        date: unseenChangeLogItems[0].date,
      });
    }
    toggleModalOpen();
  }, [unseenChangeLogItems, setLastReadChangeLogItemDate, toggleModalOpen]);

  useEffect(() => {
    loadChangeLog();
  }, [loadChangeLog]);

  if (!props.isModalOpen || !activeItem || onWebView) {
    return null;
  }

  return (
    <PortalModal
      isOpen={true}
      close={onClose}
      closeOnOverlayClick={false}
      showCloseIcon={true}
      stickyFooter={true}
      newStyle={true}
      trackingLabel="ChangeLogModal"
    >
      <ModalTitle newStyle={true} stickyFooter={true}>
        <h3>
          <FormattedMessageWrapper langKey="web.whats_new_modal.title" />
        </h3>
      </ModalTitle>
      <S.ChangeLogModalContent stickyFooter={true}>
        <ChangeLogItemRow item={activeItem} />
      </S.ChangeLogModalContent>
      <S.ChangeLogModalButtons newStyle={true} borderTop>
        {hasMoreLogItems ? (
          <PrimaryButton
            className="uppercase bold"
            langKey="web.whats_new_modal.button.next"
            onClick={setNextLogItemIndex}
            trackingLabel="NextButton"
          />
        ) : (
          <PrimaryButton
            className="uppercase bold"
            langKey="web.whats_new_modal.button.close"
            onClick={onClose}
            trackingLabel="CloseButton"
            id="modalAccept"
          />
        )}
      </S.ChangeLogModalButtons>
    </PortalModal>
  );
};

const mapDispatchToProps = {
  loadChangeLog: loadChangeLogAction,
  toggleModalOpen: toggleModalOpenAction,
  setLastReadChangeLogItemDate: setLastReadChangeLogItemDateAction,
};

const mapStateToProps = ({ changeLog }: AppState) => ({
  isModalOpen: changeLog.isModalOpen,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(ChangeLogModalContainer);
